import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useState } from 'react'
import { Trans } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import { useCreateShiftTemplateMutation } from '@/core/services/planning'
import { useGetSpecialityOptionsQuery } from '@/core/services/speciality-option'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getApiErrorMessage } from '@/utils/errors'

import { ShiftTemplateForm, ShiftTemplateFormData } from './ShiftTemplateForm'
import { browserTimezone } from '@/utils/date'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

dayjs.extend(utc)

type CreateShiftTemplateDialogProps = {
  planningId: string
  triggerVariant?: 'button' | 'icon'
}

const formId = 'create-shift-template-form'

export function CreateShiftTemplateDialog({ planningId, triggerVariant = 'button' }: CreateShiftTemplateDialogProps) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const user = useAppSelector(selectAuthUser)
  const [search, setSearch] = useState('')
  const { specialityOptions } = useGetSpecialityOptionsQuery(
    {
      smartSearch: search,
    },
    {
      selectFromResult: ({ data }) => ({ specialityOptions: data?.map((option) => ({ label: option.name, value: option.name })) ?? [] }),
      refetchOnMountOrArgChange: true,
    }
  )
  const [createShiftTemplate, { isLoading: isCreatingShiftTemplate }] = useCreateShiftTemplateMutation()

  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  const handleSubmit = useCallback(
    async (data: ShiftTemplateFormData) => {
      try {
        if (!user) {
          throw new Error('User not found')
        }

        if (!data.isRecurring && !data.date) {
          enqueueSnackbar(t('page.planning.template.dateRequired', { defaultValue: 'Please select a date for a non-recurring template.' }), { variant: 'warning' })
          return
        }

        // startTime/endTime are minutes since the start of the day, as picked (local
        // wall clock); `timezone` tells the backend which zone those minutes live in
        // so occurrences materialize as true instants (DST-aware per date).
        const base = {
          planning: planningId,
          consultationReason: data.consultationReason,
          duration: data.duration,
          startTime: data.startTime,
          endTime: data.endTime,
          timezone: browserTimezone(),
          requiredStaffCount: data.requiredStaffCount,
          location: data.location,
        }
        const payload = data.isRecurring
          ? {
              ...base,
              recurrenceRule: {
                daysOfWeek: data.recurrenceRule.daysOfWeek,
                datesOfMonth: data.recurrenceRule.datesOfMonth,
                monthsOfYear: data.recurrenceRule.monthsOfYear,
                recurrenceType: data.recurrenceRule.recurrenceType,
                separationCount: data.recurrenceRule.separationCount,
              },
            }
          : {
              // One-off template on a specific date (no recurrence). The backend builds a
              // single slot from startDate + startTime/endTime.
              ...base,
              startDate: dayjs(data.date).startOf('day').toISOString(),
              endDate: dayjs(data.date).startOf('day').toISOString(),
            }
        await createShiftTemplate(payload).unwrap()
        enqueueSnackbar(t('page.planning.template.create.success'), { variant: 'success' })
        setOpen(false)
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, t('page.planning.template.create.error')), { variant: 'error' })
      }
    },
    [user, createShiftTemplate, enqueueSnackbar, planningId, t]
  )

  const handleSearchSpeciality = useCallback(
    (search: string) => {
      setSearch(search)
    },
    [setSearch]
  )

  return (
    <>
      {triggerVariant === 'icon' ? (
        <Tooltip title={<Trans>Create template</Trans>}>
          <IconButton aria-label={t('common:button.add.label')} color="primary" data-testid="create-shift-template-icon-button" onClick={() => setOpen(true)} size="small">
            <AddIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpen(true)} fullWidth size="small">
          <Trans>Create template</Trans>
        </Button>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog open={open} onClose={() => setOpen(false)} fullScreen={isMobile} fullWidth maxWidth="sm" scroll="paper">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Trans>Create template</Trans>
            <Tooltip title={t('common:button.close.tooltip')}>
              <IconButton onClick={() => setOpen(false)}>
                <CloseRoundedIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent>
            <ShiftTemplateForm formId={formId} onSubmit={handleSubmit} specialityOptions={specialityOptions} onSearchSpeciality={handleSearchSpeciality} />
          </DialogContent>
          <DialogActions sx={{ p: (theme) => theme.spacing(2) }}>
            <LoadingButton form={formId} loading={isCreatingShiftTemplate} color="primary" disableElevation type="submit" variant="contained">
              {t('common:button.submit.label')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </>
  )
}
